import React from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Hash, Percent } from 'lucide-react';
import SearchFilterWithPills from './SearchFilterWithPills';
import type { FilterLayoutConfig } from '../services/columnConfig';
// GH#24 — caja de filtros reusable. Publicaciones delega el render de los dropdowns
// dimensionales al mismo FilterBox que consumen Estadísticas y Data Health.
// PT-#45 #1/#16 — los controles NO dimensionales (precio/posición/descuento/búsqueda)
// también se rutean por FilterBox vía `slots`, con `FieldLabel` (ícono + label) para que
// TODOS los campos compartan grilla y lenguaje visual (antes iban en markup legacy afuera).
// PT-#45 #2/#5 — la banda de título "Filtros" + saved/collapse + la caja de "Filtros
// aplicados" (pills) también viven DENTRO de FilterBox (showHeader/appliedChips).
import FilterBox, { DimensionState, FilterSlot, FilterChip } from './filters/FilterBox';
import FieldLabel from './filters/FieldLabel';
import {
  PUBLICACIONES_CONFIG,
  DimensionKey,
  FilterControlKey,
  FilterRow,
  isDimensionKey,
} from '../config/filterBoxConfigs';
import { appendDiscountToLastRow } from '../utils/filterRowsLayout';

interface UnifiedFilterPanelProps {
  // Table and column names
  selectedTable: string;
  marcaColumnName?: string;
  sitioColumnName?: string;
  categoriaColumnName?: string;

  // Filter states
  globalFilter: string;
  marcaFilter: string[];
  sitioFilter: string[];
  categoriaFilter: string[];
  appliedMarcaFilter: string[];
  appliedSitioFilter: string[];
  appliedCategoriaFilter: string[];
  // pendingRemoves — marcadas para quitar (via X de pill applied) pero aún sin aplicar.
  // Habilitan el botón Filtrar. Opcionales para retrocompatibilidad.
  pendingRemoveMarcas?: string[];
  pendingRemoveSitios?: string[];
  pendingRemoveCategorias?: string[];
  setPendingRemoveMarcas?: (value: string[]) => void;
  setPendingRemoveSitios?: (value: string[]) => void;
  setPendingRemoveCategorias?: (value: string[]) => void;
  priceFromFilter: string;
  priceToFilter: string;
  appliedPriceFromFilter: string;
  appliedPriceToFilter: string;
  maxPageFilter: string;
  maxOrderFilter: string;
  appliedMaxPageFilter: string;
  appliedMaxOrderFilter: string;
  descuentoFilter: string;
  appliedDescuentoFilter: string;
  searchTermOperators: Record<string, 'OR' | 'AND' | 'NOT'>;
  appliedSearchTermOperators: Record<string, 'OR' | 'AND' | 'NOT'>;

  // Filter options
  marcaOptions: string[];
  sitioOptions: string[];
  categoriaOptions: string[];

  // Meta opcional: counts + total. Cuando está presente, el placeholder muestra
  // "Todas las Marcas (N)" y cada opción muestra "Samsung (547)". Retro-compat:
  // si falta, se renderiza la UI vieja sin counts/total.
  marcaOptionsMeta?: { counts: Record<string, number>; total: number } | null;
  sitioOptionsMeta?: { counts: Record<string, number>; total: number } | null;
  categoriaOptionsMeta?: { counts: Record<string, number>; total: number } | null;
  subcategoryOptionsMeta?: { counts: Record<string, number>; total: number } | null;
  segmentoOptionsMeta?: { counts: Record<string, number>; total: number } | null;

  // Filter setters
  setGlobalFilter: (value: string) => void;
  setMarcaFilter: (value: string[]) => void;
  setSitioFilter: (value: string[]) => void;
  setCategoriaFilter: (value: string[]) => void;
  setAppliedMarcaFilter: (value: string[]) => void;
  setAppliedSitioFilter: (value: string[]) => void;
  setAppliedCategoriaFilter: (value: string[]) => void;
  setPriceFromFilter: (value: string) => void;
  setPriceToFilter: (value: string) => void;
  setMaxPageFilter: (value: string) => void;
  setMaxOrderFilter: (value: string) => void;
  setDescuentoFilter: (value: string) => void;
  setCategoriaOptions: (value: string[]) => void;

  // Handlers
  handlePriceFromChange: (value: string) => void;
  handlePriceToChange: (value: string) => void;
  handleSearchFilter: (searchTerms: string[], operators: Record<string, 'OR' | 'AND' | 'NOT'>) => void;

  // UI states
  showFilters: boolean;
  setShowFilters: (value: boolean) => void;
  activeFilterName?: string | null;
  activeFilterOriginal?: any;
  loadingSavedFilters?: boolean;
  /** Recalculo de counts faceted en curso → dim + spinner en los dropdowns. */
  facetsLoading?: boolean;
  resetTrigger?: number;

  // Actions
  onShowSaveFilterModal: () => void;
  getCurrentFiltersForSaving: () => any;
  hasChanges: (current: any, original: any) => boolean;
  /** PT-#57 bug2 — resetea TODOS los filtros; se rinde como "Limpiar todo" dentro de la caja. */
  onClearAllFilters?: () => void;

  // API function to load categories
  loadCategoriesApi?: (table: string) => Promise<string[]>;

  // ---- Subcategory filter (opt-in per cliente) ----
  // Activado cuando columnConfig.core.subcategory.filterable === true.
  subcategoryFilterEnabled?: boolean;
  subcategoryFilter?: string[];
  appliedSubcategoryFilter?: string[];
  pendingRemoveSubcategorias?: string[];
  setPendingRemoveSubcategorias?: (value: string[]) => void;
  subcategoryOptions?: string[];
  setSubcategoryFilter?: (value: string[]) => void;
  setAppliedSubcategoryFilter?: (value: string[]) => void;
  setSubcategoryOptions?: (value: string[]) => void;
  loadSubcategoriesApi?: (table: string) => Promise<string[]>;

  // ---- Segmento filter (opt-in per cliente) ----
  // Activado cuando columnConfig.core.segmento.filterable === true.
  segmentoFilterEnabled?: boolean;
  segmentoFilter?: string[];
  appliedSegmentoFilter?: string[];
  pendingRemoveSegmentos?: string[];
  setPendingRemoveSegmentos?: (value: string[]) => void;
  segmentoOptions?: string[];
  setSegmentoFilter?: (value: string[]) => void;
  setAppliedSegmentoFilter?: (value: string[]) => void;
  setSegmentoOptions?: (value: string[]) => void;
  loadSegmentosApi?: (table: string) => Promise<string[]>;

  // ---- Filter Layout (configurable per cliente) ----
  filterLayout?: FilterLayoutConfig;

  // Active tab to conditionally show/hide filters
  activeTab?: string;
}

const UnifiedFilterPanel: React.FC<UnifiedFilterPanelProps> = ({
  selectedTable,
  marcaColumnName,
  sitioColumnName,
  categoriaColumnName,
  globalFilter,
  marcaFilter,
  sitioFilter,
  categoriaFilter,
  appliedMarcaFilter,
  pendingRemoveMarcas = [],
  setPendingRemoveMarcas,
  pendingRemoveSitios = [],
  setPendingRemoveSitios,
  pendingRemoveCategorias = [],
  setPendingRemoveCategorias,
  appliedSitioFilter,
  appliedCategoriaFilter,
  priceFromFilter,
  priceToFilter,
  appliedPriceFromFilter,
  appliedPriceToFilter,
  maxPageFilter,
  maxOrderFilter,
  appliedMaxPageFilter,
  appliedMaxOrderFilter,
  descuentoFilter,
  appliedDescuentoFilter,
  searchTermOperators,
  appliedSearchTermOperators,
  marcaOptions,
  sitioOptions,
  categoriaOptions,
  marcaOptionsMeta = null,
  sitioOptionsMeta = null,
  categoriaOptionsMeta = null,
  subcategoryOptionsMeta = null,
  segmentoOptionsMeta = null,
  setGlobalFilter,
  setMarcaFilter,
  setSitioFilter,
  setCategoriaFilter,
  setAppliedMarcaFilter,
  setAppliedSitioFilter,
  setAppliedCategoriaFilter,
  setPriceFromFilter,
  setPriceToFilter,
  setMaxPageFilter,
  setMaxOrderFilter,
  setDescuentoFilter,
  setCategoriaOptions,
  handlePriceFromChange,
  handlePriceToChange,
  handleSearchFilter,
  showFilters,
  setShowFilters,
  activeFilterName,
  activeFilterOriginal,
  loadingSavedFilters = false,
  facetsLoading = false,
  resetTrigger,
  onShowSaveFilterModal,
  getCurrentFiltersForSaving,
  hasChanges,
  onClearAllFilters,
  loadCategoriesApi,
  subcategoryFilterEnabled = false,
  subcategoryFilter = [],
  appliedSubcategoryFilter = [],
  pendingRemoveSubcategorias = [],
  setPendingRemoveSubcategorias,
  subcategoryOptions = [],
  setSubcategoryFilter,
  setAppliedSubcategoryFilter,
  setSubcategoryOptions,
  loadSubcategoriesApi,
  segmentoFilterEnabled = false,
  segmentoFilter = [],
  appliedSegmentoFilter = [],
  pendingRemoveSegmentos = [],
  setPendingRemoveSegmentos,
  segmentoOptions = [],
  setSegmentoFilter,
  setAppliedSegmentoFilter,
  setSegmentoOptions,
  loadSegmentosApi,
  filterLayout,
  activeTab
}) => {
  const { t } = useTranslation();

  // Auto-load de options al abrir cada dropdown. Antes lo hacían useEffect keyed por
  // showXDropdown; ahora lo dispara FilterDropdown vía onOpen (misma condición/guarda).
  const handleOpenCategoria = () => {
    if (categoriaOptions.length === 0 && selectedTable && loadCategoriesApi) {
      loadCategoriesApi(selectedTable).then(setCategoriaOptions).catch(() => {});
    }
  };
  const handleOpenSubcategoria = () => {
    if (subcategoryFilterEnabled && subcategoryOptions.length === 0 && selectedTable && loadSubcategoriesApi && setSubcategoryOptions) {
      loadSubcategoriesApi(selectedTable).then(setSubcategoryOptions).catch(() => {});
    }
  };
  const handleOpenSegmento = () => {
    if (segmentoFilterEnabled && segmentoOptions.length === 0 && selectedTable && loadSegmentosApi && setSegmentoOptions) {
      loadSegmentosApi(selectedTable).then(setSegmentoOptions).catch(() => {});
    }
  };

  const noopSetFilter = (_value: string[]) => {};

  // State + options por dimensión que consume FilterBox (GH#24). Publicaciones reusa el
  // state que ya vive en Dashboard; el pending-removal y los counts quedan intactos.
  const boxDimensions: Partial<Record<DimensionKey, DimensionState>> = {
    brand: {
      filter: marcaFilter, applied: appliedMarcaFilter, pendingRemove: pendingRemoveMarcas,
      setFilter: setMarcaFilter, setPendingRemove: setPendingRemoveMarcas,
      onRemoveApplied: (v) => {
        setAppliedMarcaFilter(appliedMarcaFilter.filter(x => x !== v));
        setPendingRemoveMarcas?.(pendingRemoveMarcas.filter(x => x !== v));
      },
      options: marcaOptions, meta: marcaOptionsMeta, enabled: true,
    },
    site: {
      filter: sitioFilter, applied: appliedSitioFilter, pendingRemove: pendingRemoveSitios,
      setFilter: setSitioFilter, setPendingRemove: setPendingRemoveSitios,
      onRemoveApplied: (v) => {
        setAppliedSitioFilter(appliedSitioFilter.filter(x => x !== v));
        setPendingRemoveSitios?.(pendingRemoveSitios.filter(x => x !== v));
      },
      options: sitioOptions, meta: sitioOptionsMeta, enabled: true,
    },
    category: {
      filter: categoriaFilter, applied: appliedCategoriaFilter, pendingRemove: pendingRemoveCategorias,
      setFilter: setCategoriaFilter, setPendingRemove: setPendingRemoveCategorias,
      onRemoveApplied: (v) => {
        setAppliedCategoriaFilter(appliedCategoriaFilter.filter(x => x !== v));
        setPendingRemoveCategorias?.(pendingRemoveCategorias.filter(x => x !== v));
      },
      options: categoriaOptions, meta: categoriaOptionsMeta, enabled: true, onOpen: handleOpenCategoria,
    },
    subcategory: {
      filter: subcategoryFilter, applied: appliedSubcategoryFilter, pendingRemove: pendingRemoveSubcategorias,
      setFilter: setSubcategoryFilter || noopSetFilter, setPendingRemove: setPendingRemoveSubcategorias,
      onRemoveApplied: (v) => {
        if (setAppliedSubcategoryFilter) setAppliedSubcategoryFilter(appliedSubcategoryFilter.filter(x => x !== v));
        setPendingRemoveSubcategorias?.(pendingRemoveSubcategorias.filter(x => x !== v));
      },
      options: subcategoryOptions, meta: subcategoryOptionsMeta, enabled: subcategoryFilterEnabled, onOpen: handleOpenSubcategoria,
    },
    segment: {
      filter: segmentoFilter, applied: appliedSegmentoFilter, pendingRemove: pendingRemoveSegmentos,
      setFilter: setSegmentoFilter || noopSetFilter, setPendingRemove: setPendingRemoveSegmentos,
      onRemoveApplied: (v) => {
        if (setAppliedSegmentoFilter) setAppliedSegmentoFilter(appliedSegmentoFilter.filter(x => x !== v));
        setPendingRemoveSegmentos?.(pendingRemoveSegmentos.filter(x => x !== v));
      },
      options: segmentoOptions, meta: segmentoOptionsMeta, enabled: segmentoFilterEnabled, onOpen: handleOpenSegmento,
    },
  };

  // Renglones dimensionales: del filterLayout per-cliente si existe; si no, default
  // (brand+site / category+subcategory+segment).
  const baseDimRows: FilterRow[] = (filterLayout && filterLayout.rows.length > 0)
    ? filterLayout.rows.map(r => ({
        fields: r.fields.map(f => ({ key: f.key as FilterControlKey, enabled: f.enabled, widthPct: f.widthPct })),
      }))
    : PUBLICACIONES_CONFIG.rows
        .filter(row => row.fields.every(f => isDimensionKey(f.key)))
        .map(row => ({ fields: row.fields.map(f => ({ ...f })) }));

  // PT-#57 R2-1 — DESCUENTO va como ÚLTIMO item de la ÚLTIMA fila dimensional (para Samsung
  // = Cat/Subcat/Segmento → Cat/Subcat/Segmento/Descuento), NO en una fila suelta. Se rinde
  // vía slot. PT-#87 F1 — si los campos dibujados + Descuentos (25%) entran en 100% se
  // respetan los `widthPct` del layout; si no, recálculo legacy a 100/N (ver
  // utils/filterRowsLayout). "Dibujado" = mismo criterio que FilterBox.renderDimension.
  const dimRows: FilterRow[] = appendDiscountToLastRow(
    baseDimRows,
    f => f.enabled && (!isDimensionKey(f.key) || !!boxDimensions[f.key]?.enabled),
  );

  // PT-#45 #1/#16 — renglones NO dimensionales (precio/posición/búsqueda) del config
  // canónico. Se rutean por el MISMO FilterBox (grilla + labels con ícono), ya no en markup
  // legacy afuera de la caja. El filterLayout per-cliente solo reordena los dimensionales;
  // estos van siempre al final con su layout de config. (Descuento ya no vive acá: R2-1.)
  const nonDimRows: FilterRow[] = PUBLICACIONES_CONFIG.rows.filter(
    row => !row.fields.every(f => isDimensionKey(f.key)),
  );
  const allRows: FilterRow[] = [...dimRows, ...nonDimRows];

  // Slots de los controles no dimensionales. Cada uno recibe el `widthStyle` (grilla de
  // FilterBox) y rinde su propio `FieldLabel` con ícono para concordar con los dropdowns.
  const filterSlots: Partial<Record<FilterControlKey, FilterSlot>> = {
    price: (widthStyle) => (
      <div style={widthStyle}>
        <FieldLabel icon={<DollarSign className="h-3.5 w-3.5" />}>{t('filters.price_range')}</FieldLabel>
        <div className="flex space-x-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
            <input
              type="number"
              value={priceFromFilter}
              onChange={(e) => handlePriceFromChange(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('filters.from')}
              min="0"
            />
          </div>
          <span className="flex items-center text-gray-400">-</span>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
            <input
              type="number"
              value={priceToFilter}
              onChange={(e) => handlePriceToChange(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('filters.to')}
              min="0"
            />
          </div>
        </div>
      </div>
    ),
    position: (widthStyle) => (
      <div style={widthStyle}>
        <FieldLabel icon={<Hash className="h-3.5 w-3.5" />}>{t('filters.max_position_label')}</FieldLabel>
        <div className="flex space-x-2">
          <input
            type="number"
            value={maxPageFilter}
            onChange={(e) => setMaxPageFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t('filters.page')}
          />
          <span className="flex items-center text-gray-400">-</span>
          <input
            type="number"
            value={maxOrderFilter}
            onChange={(e) => setMaxOrderFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t('filters.order')}
          />
        </div>
      </div>
    ),
    discount: (widthStyle) => (
      <div style={widthStyle}>
        <FieldLabel icon={<Percent className="h-3.5 w-3.5" />}>{t('filters.discounts')}</FieldLabel>
        <select
          value={descuentoFilter}
          onChange={(e) => setDescuentoFilter(e.target.value)}
          className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">{t('filters.all_products')}</option>
          <option value="with_discount">{t('filters.with_discounts')}</option>
          <option value="without_discount">{t('filters.without_discounts')}</option>
        </select>
      </div>
    ),
    globalSearch: (widthStyle) => (
      <div style={widthStyle ?? { width: '100%' }}>
        {/* El label "Búsqueda global" + ícono lo rinde SearchFilterWithPills internamente. */}
        <SearchFilterWithPills
          onSearch={handleSearchFilter}
          placeholder={t('filters.search_placeholder')}
          resetTrigger={resetTrigger}
          initialPills={(() => {
            if (!globalFilter) return [];
            // No dividir el filtro global en palabras separadas; tratarlo como término único.
            return [{
              id: `pill-${globalFilter}-0`,
              text: globalFilter,
              operator: (searchTermOperators[globalFilter] || appliedSearchTermOperators[globalFilter] || 'AND') as 'OR' | 'AND' | 'NOT',
            }];
          })()}
          externalOperators={searchTermOperators}
          hasExternalChanges={(() => {
            const hasChanges = priceFromFilter !== appliedPriceFromFilter ||
              priceToFilter !== appliedPriceToFilter ||
              maxPageFilter !== appliedMaxPageFilter ||
              maxOrderFilter !== appliedMaxOrderFilter ||
              descuentoFilter !== appliedDescuentoFilter ||
              marcaFilter.length > 0 ||
              sitioFilter.length > 0 ||
              categoriaFilter.length > 0 ||
              subcategoryFilter.length > 0 ||
              segmentoFilter.length > 0 ||
              pendingRemoveMarcas.length > 0 ||
              pendingRemoveSitios.length > 0 ||
              pendingRemoveCategorias.length > 0 ||
              pendingRemoveSubcategorias.length > 0 ||
              pendingRemoveSegmentos.length > 0 ||
              JSON.stringify(searchTermOperators) !== JSON.stringify(appliedSearchTermOperators);
            return hasChanges;
          })()}
        />
      </div>
    ),
  };

  // PT-#45 #5 — Chips NO dimensionales (rango de precio/posición/descuento/búsqueda) que
  // alimentan la caja "Filtros aplicados" de FilterBox. Los dimensionales los deriva
  // FilterBox solo desde `boxDimensions` (fuente única); acá solo van los numéricos/búsqueda,
  // leídos del state YA aplicado (applied*), no del pendiente. Quitar un chip limpia ese
  // filtro aplicado (y el pendiente asociado, para no dejar el botón Filtrar colgado).
  const appliedChips: FilterChip[] = [];
  if (appliedPriceFromFilter || appliedPriceToFilter) {
    const from = appliedPriceFromFilter ? `$${appliedPriceFromFilter}` : '';
    const to = appliedPriceToFilter ? `$${appliedPriceToFilter}` : '';
    const range = from && to ? `${from} - ${to}` : from ? `≥ ${from}` : `≤ ${to}`;
    appliedChips.push({
      id: 'price-range', label: `${t('filters.price_range')}: ${range}`,
      onRemove: () => { setPriceFromFilter(''); setPriceToFilter(''); handlePriceFromChange(''); handlePriceToChange(''); },
    });
  }
  if (appliedMaxPageFilter || appliedMaxOrderFilter) {
    const parts = [appliedMaxPageFilter && `${t('filters.page')} ${appliedMaxPageFilter}`, appliedMaxOrderFilter && `${t('filters.order')} ${appliedMaxOrderFilter}`].filter(Boolean).join(', ');
    appliedChips.push({
      id: 'position', label: `${t('filters.max_position_label')}: ${parts}`,
      onRemove: () => { setMaxPageFilter(''); setMaxOrderFilter(''); },
    });
  }
  if (appliedDescuentoFilter && appliedDescuentoFilter !== 'all') {
    const dlabel = appliedDescuentoFilter === 'with_discount' ? t('filters.with_discounts') : t('filters.without_discounts');
    appliedChips.push({
      id: 'discount', label: `${t('filters.discounts')}: ${dlabel}`,
      onRemove: () => setDescuentoFilter('all'),
    });
  }

  return (
    // PT-#45 #2/#5 — La banda de título "Filtros" (con botón Filtros Guardados + mostrar/
    // ocultar) y la caja de "Filtros aplicados" (pills) ahora las rinde FilterBox
    // (showHeader/appliedChips), compartidas con Estadísticas/Tendencias. allRows =
    // dimensionales (respetan filterLayout per-cliente + treeMode) seguidos de los NO
    // dimensionales (precio/posición/descuento/búsqueda) que llegan por `slots`.
    <FilterBox
      config={{ ...PUBLICACIONES_CONFIG, rows: allRows }}
      t={t}
      dimensions={boxDimensions}
      slots={filterSlots}
      loading={facetsLoading}
      showHeader
      headerTitle={t('filters.title')}
      activeFilterBadge={activeFilterName ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {activeFilterName}
          {(activeFilterOriginal && hasChanges(getCurrentFiltersForSaving(), activeFilterOriginal)) && (
            <span className="ml-1 text-blue-600">*</span>
          )}
        </span>
      ) : undefined}
      onOpenSavedFilters={onShowSaveFilterModal}
      savedFiltersLoading={loadingSavedFilters}
      collapsed={!showFilters}
      onToggleCollapse={() => setShowFilters(!showFilters)}
      appliedChips={appliedChips}
      onClearAll={onClearAllFilters}
    />
  );
};

export default UnifiedFilterPanel;
